import type { AgentV2LinkRenderer } from './AgentV2Markdown'
import SavedUserSkills from '@/user-skills/components/SavedUserSkills'
import clsx from 'clsx'
import { useRef, type ReactNode } from 'react'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import type { AgentV2Message, AgentV2MessageAttachedFiles, AgentV2WorkLogItem } from '@/agent-v2/store/reducer'
import { AgentV2Markdown } from '@/agent-v2/components/AgentV2Markdown'
import { DocxExportNotice } from '@/agent-v2/docx-export/components/DocxExportNotice'
import { MatterArtifactVersions } from '@/matter-artifacts/components/MatterArtifactVersions'
import { AgentV2FollowUpQuestions } from '@/agent-v2/components/AgentV2FollowUpQuestions'
import { AgentV2MessageReferences } from '@/agent-v2/components/AgentV2MessageReferences'
import { AgentV2RunProgress } from '@/agent-v2/components/AgentV2RunProgress'
import { UserMessageBubble } from '@/agent-v2/components/UserMessageBubble'
import { TurnAttachedFiles } from '@/agent-v2/components/TurnAttachedFiles'
import AgentV2ResponseActions from '@/agent-v2/components/AgentV2ResponseActions'
import { selectAgentV2TurnFeedback } from '@/agent-v2/conversations/store/selectors'
import { buildAgentV2ReferenceSections } from '@/agent-v2/references/agent-v2-reference-sections'
import { selectAgentV2ReferencesByCitationId } from '@/agent-v2/references/store/selectors'
import { selectAgentV2LatestMatterArtifactVersionIds } from '@/agent-v2/store/selectors'
import { useConversationActiveCitationId } from '@/agent-v2/hooks/useConversationActiveCitationId'
import type { AgentV2TurnFeedback } from '@/agent-v2/feedback/schemas'
import { copyAgentV2Response } from '@/agent-v2/utils/copy-agent-v2-response'
import type { MatterArtifactVersionRead } from '@/matter-artifacts/schemas'
import { selectMatterArtifactVersionById } from '@/matter-artifacts/store/selectors'
import type { RootState } from '@/store/store'
import { useAppSelector } from '@/store/store-hooks'

/** A host-specific presentation of one response, preserving shared citations, copy and feedback. */
export type AgentV2ResponsePresentation = {
  turnId: string
  markdown: string
  renderBody: (body: ReactNode) => ReactNode
}

export type AgentV2MessageBubbleProps = {
  conversationId: string
  message: AgentV2Message
  onCitationClick: (citationId: string) => void
  /** Whether onCitationClick opens sources in the in-app content viewer (useAgentV2ReferenceOpensInPanel). */
  referencesOpenInPanel?: boolean
  renderResponseFooter?: (message: AgentV2Message) => ReactNode
  renderLink?: AgentV2LinkRenderer
  onFileLinkClick?: (href: string, title: string) => boolean
  onCaseStatusClick: (citationId: string) => void
  /** Presence enables the inline treatment flags beside warning-status citations. */
  onTreatmentFlagClick?: (citationId: string) => void
  onFollowUpQuestionClick: (question: string) => void
  onMatterArtifactCancel: () => void
  onMatterArtifactView: (matterArtifactVersion: MatterArtifactVersionRead) => void
  onMatterArtifactViewCurrent: (matterArtifactVersion: MatterArtifactVersionRead) => void
  onFeedbackChange: (feedback: AgentV2TurnFeedback) => void
  progressRunning: boolean
  readOnly: boolean
  workLogItems: AgentV2WorkLogItem[]
  onDownloadConversation?: () => Promise<void>
  responsePresentation?: AgentV2ResponsePresentation
  compactProgress?: boolean
  formatUserMessage?: (text: string) => string
  showReferenceList?: boolean
  /** The files a user message newly attached, shown as chips above it. */
  addedFiles?: AgentV2MessageAttachedFiles
}

export function AgentV2MessageBubble({
  message,
  conversationId,
  onCitationClick,
  referencesOpenInPanel = false,
  onFileLinkClick,
  renderLink,
  renderResponseFooter,
  onCaseStatusClick,
  onTreatmentFlagClick,
  onFollowUpQuestionClick,
  onMatterArtifactCancel,
  onMatterArtifactView,
  onMatterArtifactViewCurrent,
  onFeedbackChange,
  progressRunning,
  readOnly,
  workLogItems,
  onDownloadConversation,
  responsePresentation,
  compactProgress = false,
  formatUserMessage,
  showReferenceList = true,
  addedFiles,
}: AgentV2MessageBubbleProps) {
  const feedback = useAppSelector((state: RootState) => selectAgentV2TurnFeedback(state, { turnId: message.turnId }))
  const referencesByCitationId = useAppSelector((state: RootState) => selectAgentV2ReferencesByCitationId(state, { conversationId }))
  const activeCitationId = useConversationActiveCitationId()
  const latestMatterArtifactVersionIds = useAppSelector((state: RootState) => selectAgentV2LatestMatterArtifactVersionIds(state, { conversationId }))
  const selectedMatterArtifactVersion = useAppSelector((state: RootState) =>
    selectMatterArtifactVersionById(state, message.matterArtifactSelection?.matter_artifact_version_id)
  )
  const conversationMatterId = useAppSelector((state: RootState) => state.agentV2ConversationsState.details[conversationId]?.conversation.matter_id ?? null)
  const responseElementRef = useRef<HTMLDivElement>(null)

  if (message.role === 'user') {
    const bubble = (
      <UserMessageBubble
        artifactName={selectedMatterArtifactVersion?.name ?? 'Matter artifact'}
        matterArtifactSelection={message.matterArtifactSelection}
        attachedSkillNames={message.attachedSkillNames}
        text={formatUserMessage?.(message.text) ?? message.text}
      />
    )
    if (!addedFiles) return bubble

    return (
      <div className="flex flex-col items-end gap-3">
        <TurnAttachedFiles conversationId={conversationId} attachedFiles={addedFiles} />
        {bubble}
      </div>
    )
  }

  // Export as .docx notices are server-written status, not answers: a card, with no response actions.
  if (message.docxExport) {
    return (
      <div className="agent-v2-assistant-message px-4 py-2">
        {/* A notice's turn starts when the server writes it, so a started notice's turn start is the export's. */}
        <DocxExportNotice notice={message.docxExport} matterId={conversationMatterId} startedAt={message.runStartedAt} conversationId={conversationId} />
      </div>
    )
  }

  const presentation = responsePresentation?.turnId === message.turnId ? responsePresentation : undefined
  const reasoningRunning = progressRunning || Boolean(message.runStartedAt && !message.runEndedAt && !message.isError && !message.isStopped)
  const durationSeconds =
    message.runStartedAt && message.runEndedAt ? Math.max(0, Math.round((Date.parse(message.runEndedAt) - Date.parse(message.runStartedAt)) / 1000)) : undefined
  const responseText = presentation?.markdown ?? message.text
  const responseBody = responseText ? (
    <div ref={responseElementRef}>
      <div className="agent-v2-response-text mb-2 text-sm leading-6">
        <div className="max-w-3xl" id="chat-response-bubble">
          <AgentV2Markdown
            text={responseText}
            referencesByCitationId={referencesByCitationId}
            activeCitationId={activeCitationId}
            onCitationClick={onCitationClick}
            referencesOpenInPanel={referencesOpenInPanel}
            onFileLinkClick={onFileLinkClick}
            renderLink={renderLink}
            onCaseStatusClick={onCaseStatusClick}
            onTreatmentFlagClick={onTreatmentFlagClick}
            withCitationHoverCard
            isStreaming={message.isStreaming}
          />
        </div>
      </div>
    </div>
  ) : null

  return (
    <div
      className="agent-v2-assistant-message rounded-lg bg-transparent px-4 py-2 text-base whitespace-pre-wrap compatible-word-break"
      // Both signals, because a connection recovery ends the live run without clearing the flag.
      data-streaming={message.isStreaming && progressRunning ? 'true' : undefined}
    >
      {(workLogItems.length > 0 || reasoningRunning) && (
        <AgentV2RunProgress running={reasoningRunning} workLogItems={workLogItems} compact={compactProgress} durationSeconds={durationSeconds} />
      )}
      {!presentation && message.matterArtifactVersionIds && message.matterArtifactVersionIds.length > 0 && (
        <MatterArtifactVersions
          matterArtifactVersionIds={message.matterArtifactVersionIds}
          latestMatterArtifactVersionIds={latestMatterArtifactVersionIds}
          canCancelGenerating={progressRunning && !readOnly}
          onCancelGenerating={onMatterArtifactCancel}
          onViewMatterArtifact={onMatterArtifactView}
          onViewCurrentMatterArtifact={onMatterArtifactViewCurrent}
        />
      )}
      {!readOnly && message.turnId && <SavedUserSkills conversationId={conversationId} turnId={message.turnId} />}
      {/* Live only while this message is actively streaming so screen readers hear tokens as they
          arrive; hydrated/completed answers must not be re-announced on mount (ENG-7077) */}
      <div aria-live={progressRunning ? 'polite' : undefined}>{presentation ? presentation.renderBody(responseBody) : responseBody}</div>
      {renderResponseFooter?.(message)}
      {message.errorText && (
        <div className={clsx(message.text && 'mt-3 border-t border-brand-neutral-200 pt-2')}>
          <InlineMessage variant="error">{message.errorText}</InlineMessage>
        </div>
      )}
      {message.isStopped && (
        <div className="mt-3 border-t border-brand-neutral-200 pt-2 text-xs font-medium text-brand-neutral-600" role="status">
          Response stopped
        </div>
      )}
      {responseText && !message.isStreaming && message.turnId && (
        <AgentV2ResponseActions
          conversationId={conversationId}
          feedback={feedback}
          feedbackDisabledReason={readOnly ? 'Only the chat creator can rate this response.' : null}
          isFeedbackEligible={message.canReceiveFeedback ?? false}
          onCopy={() => {
            if (!responseElementRef.current || !message.turnId) throw new Error('The response is unavailable to copy.')
            return copyAgentV2Response(
              responseElementRef.current,
              buildAgentV2ReferenceSections({ messageText: responseText, turnId: message.turnId, referencesByCitationId })
            )
          }}
          onFeedbackChange={onFeedbackChange}
          onDownload={onDownloadConversation}
          turnId={message.turnId}
        />
      )}
      {showReferenceList && responseText && !message.isStreaming && message.turnId && (
        <AgentV2MessageReferences messageText={responseText} referencesByCitationId={referencesByCitationId} turnId={message.turnId} />
      )}
      {!readOnly && <AgentV2FollowUpQuestions assistantMessageId={message.id} onQuestionClick={onFollowUpQuestionClick} />}
    </div>
  )
}
